<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from 'vue';
import { useMediaQuery } from '@vueuse/core';
import { useRecentItemsStore } from '@/stores/recentItemsStore';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
import { AxiosError } from 'axios';
import { toast } from 'vue-sonner';
import { ArrowLeftIcon, ChevronDownIcon, ChevronUpIcon, LinkIcon, UserGroupIcon } from '@heroicons/vue/24/outline';
import { LazyTooltip } from '@/components/ui/tooltip';
import { buildEntityColumns } from '@/composables/entityColumns';
import { useModelRecordQuery } from '@/composables/api/useModelRecordQuery';
import { readRecordListContext, recordListState, stepPosition } from '@/composables/useRecordListContext';
import { useRecordLayout } from '@/composables/recordLayout/useRecordLayout';
import { recordTitle } from '@/composables/recordTitle';
import CustomizeRecordDialog from '@/components/record/CustomizeRecordDialog.vue';
import RecordMoreMenu from '@/components/record/RecordMoreMenu.vue';
import RecordBody from '@/components/record/RecordBody.vue';
import SyncStatusButton from '@/components/Sync/SyncStatusButton.vue';
import { copyText } from '@/utils/nativeShell';
import { showsSyncPill } from '@/utils/syncStatus';
import SidebarToggle from '@/layouts/frame/SidebarToggle.vue';
import PageMenu from '@/layouts/frame/PageMenu.vue';
import { useAreaPicker } from '@/composables/useAreaPicker';
import { splitListPath } from '@/navigation/areaPicker';
import { areaIdForRoute } from '@/navigation/activeArea';
import { useAuthStore } from '@/stores/authStore';
import { areaEntity, listPathFor, nounInSentence, recordPathFor } from '@/navigation/areas';

/**
 * A record at its own address, full width, for example /players/1.
 *
 * Its body, the name, chips and sections, is RecordBody, the same body the side
 * peek shows. The page adds what a page needs around it: the list it came from,
 * as a link back and a menu of the area's other lists,
 * "1 of 20" with arrows to step through that list, chips for the fields people
 * check most, real tabs kept in the address (`?tab=logs`), and a way to open
 * the record in a peek instead.
 */

const route = useRoute();
const router = useRouter();

const entityId = computed(() => String(route.meta.entityId ?? ''));
const recordId = computed(() => Number(route.params.id));
const entity = computed(() => areaEntity(entityId.value));
const noun = computed(() => entity.value?.noun ?? 'Record');

const columns = computed(() => buildEntityColumns(entityId.value));

// The Sync pill, on the record pages of the lists that show it. This header has
// no fit ladder, so the pill shows its counts on desktop and its icon on a phone.
const hasSyncPill = computed(() => showsSyncPill(entityId.value));
const isDesktop = useMediaQuery('(min-width: 768px)');
const syncPillForm = computed(() => (isDesktop.value ? 'full' : 'icon'));

const recordQuery = useModelRecordQuery(entityId, recordId);
const row = computed(() => recordQuery.data.value ?? null);

/** A 404 or 403 means the record is gone or not yours to see. Anything else can be retried. */
const isNotFound = computed(() => {
    const error = recordQuery.error.value;
    return error instanceof AxiosError && (error.response?.status === 404 || error.response?.status === 403);
});

const title = computed(() => recordTitle(row.value, noun.value, recordId.value));

// Opened records go into Home's and Cmd+K's recent list; a record that turns
// out not to exist leaves it.
// Remembered once per record opened, not on every refresh: a player's data is
// polled every few seconds, and each poll would otherwise rewrite the stored
// list and move the record's "opened" time forward while it simply stays open.
const recentItems = useRecentItemsStore();
let rememberedKey: string | null = null;
watch([row, isNotFound], ([loaded, notFound]) => {
    const key = `${entityId.value}:${recordId.value}`;
    if (notFound) {
        recentItems.forget(entityId.value, recordId.value);
        rememberedKey = null;
    } else if (loaded && rememberedKey !== key) {
        recentItems.remember({ kind: 'record', entityId: entityId.value, id: recordId.value, label: title.value });
        rememberedKey = key;
    }
});

// Your layout, else Everyone's default, else the built-in one. While a developer
// edits the default, the page previews the draft instead.
const authStore = useAuthStore();
const isDeveloper = computed(() => Boolean(authStore.userData?.is_developer));
const layoutState = useRecordLayout(entityId, columns, { onForbidden: () => void authStore.loadUser() });
const layout = computed(() => layoutState.shown.value);
const customizeOpen = ref(false);
const moreMenu = useTemplateRef<InstanceType<typeof RecordMoreMenu>>('moreMenu');

// An unsaved default would be lost, so ask before leaving with one.
onBeforeRouteLeave(() => {
    if (layoutState.isEditingDefault.value && !window.confirm('Leave without saving the default layout?')) {
        return false;
    }
    layoutState.cancelDefault();
    return true;
});
function onSectionChange(section: string) {
    router.replace({ query: { ...route.query, tab: section }, state: history.state });
}

const initialSection = computed(() => (typeof route.query.tab === 'string' ? route.query.tab : null));

// The list this record was opened from, kept in history state. A pasted
// address has none, so the breadcrumb falls back to the entity's list and
// "1 of 20" is hidden.
const listContext = computed(() => readRecordListContext(history.state));
const breadcrumb = computed(() => ({
    label: listContext.value?.label ?? entity.value?.label ?? 'List',
    path: listContext.value?.path ?? listPathFor(entityId.value),
}));
const position = computed(() => stepPosition(listContext.value, recordId.value));

// The menu beside the list's name marks that list current, or the saved view
// when the record was opened from one. The record's own name is not in the
// header: it is the page's big heading.
const listLocation = computed(() => splitListPath(breadcrumb.value.path));
const picker = useAreaPicker(
    () => areaIdForRoute(route),
    () => ({ routeName: '', path: listLocation.value.path, entityId: entityId.value, viewId: listLocation.value.viewId }),
);
// Opened from a saved view that is not among the loaded views, so it was
// deleted since. While the views load, or if they fail to, nothing is known
// yet, and the header keeps the name the record was opened with.
const openedViewIsGone = computed(
    () => listLocation.value.viewId !== null && picker.value.matchedView === null && picker.value.viewsAreKnown,
);
// A view renamed since the record was opened shows its new name. A deleted
// one falls back to the list it belonged to, as the list header does.
const listLabel = computed(() => {
    if (picker.value.matchedView) {
        return picker.value.matchedView.name;
    }
    if (openedViewIsGone.value) {
        return picker.value.label || breadcrumb.value.label;
    }
    return breadcrumb.value.label;
});
const listTo = computed(() => (openedViewIsGone.value ? listPathFor(entityId.value) : breadcrumb.value.path));

function step(targetId: number | null) {
    if (targetId === null || !listContext.value) return;
    router.replace({ path: recordPathFor(entityId.value, targetId), query: {}, state: recordListState(listContext.value) });
}

async function copyLink() {
    if (await copyText(window.location.href)) {
        toast.success('Link copied.');
    } else {
        toast.error(`Couldn't copy the link. It is: ${window.location.href}`);
    }
}
</script>

<template>
  <div class="flex min-h-0 min-w-0 flex-1 flex-col">
    <header class="flex h-12 shrink-0 items-center justify-between gap-3 px-4" data-testid="record-header">
      <nav aria-label="Breadcrumb" class="flex min-w-0 items-center gap-1.5 text-sm">
        <SidebarToggle class="-ml-1.5" />
        <!-- On a phone a back arrow takes the breadcrumb's place. -->
        <LazyTooltip side="bottom" align="start">
          <router-link :to="listTo" :aria-label="`Back to ${listLabel}`" class="flex size-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground md:hidden" data-testid="record-back">
            <ArrowLeftIcon class="size-5" aria-hidden="true" />
          </router-link>
          <template #content>Back to {{ listLabel }}</template>
        </LazyTooltip>
        <div class="hidden min-w-0 items-center md:flex" data-testid="record-breadcrumb-list">
          <PageMenu :picker="picker" :label="listLabel" :label-to="listTo" />
        </div>
        <template v-if="position">
          <span class="ml-2 shrink-0 text-xs text-muted-foreground" data-testid="record-position">{{ position.position }} of {{ position.total }}</span>
          <!-- Each button sits in a span. A disabled button gets no pointer events,
               so at the first or last record the span is what shows the tooltip. -->
          <LazyTooltip side="bottom">
            <span class="flex" data-testid="record-previous-tooltip-target">
              <button
                type="button"
                aria-label="Previous record"
                data-testid="record-previous"
                class="flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
                :disabled="position.previousId === null"
                @click="step(position.previousId)"
              >
                <ChevronUpIcon class="size-4" aria-hidden="true" />
              </button>
            </span>
            <template #content>Previous record</template>
          </LazyTooltip>
          <LazyTooltip side="bottom">
            <span class="flex" data-testid="record-next-tooltip-target">
              <button
                type="button"
                aria-label="Next record"
                data-testid="record-next"
                class="flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
                :disabled="position.nextId === null"
                @click="step(position.nextId)"
              >
                <ChevronDownIcon class="size-4" aria-hidden="true" />
              </button>
            </span>
            <template #content>Next record</template>
          </LazyTooltip>
        </template>
      </nav>
      <div class="flex shrink-0 items-center gap-1 text-sm">
        <SyncStatusButton v-if="hasSyncPill" :form="syncPillForm" />
        <LazyTooltip side="bottom">
          <button
            type="button"
            aria-label="Copy link"
            data-testid="record-copy-link"
            class="flex size-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
            @click="copyLink"
          >
            <LinkIcon class="size-4" aria-hidden="true" />
          </button>
          <template #content>Copy link</template>
        </LazyTooltip>
        <!-- Open in Admin app, Copy ID and Customize page…. Customize waits for the
             record, so the dialog has something to show. -->
        <RecordMoreMenu
          ref="moreMenu"
          :entity-id="entityId"
          :record-id="recordId"
          :noun="noun"
          :can-customize="Boolean(row)"
          test-id-prefix="record-more"
          @customize="customizeOpen = true"
        />
      </div>
    </header>

    <div class="flex min-h-0 flex-1">
    <div class="min-h-0 flex-1 overflow-y-auto pb-12 max-md:pb-mobile-area-bar-clearance" data-testid="record-scroll-area">
      <p
        v-if="layoutState.isEditingDefault.value"
        class="flex h-9 items-center justify-center gap-2 bg-accent/20 text-sm font-medium"
        data-testid="record-default-preview"
      >
        <UserGroupIcon class="size-4" aria-hidden="true" />
        Previewing the default {{ noun.toLowerCase() }} page for everyone
      </p>
      <!-- One centered column, as Macro lays out a record: 1024 pixels wide at
           most, with the spare width split evenly on both sides. As the window
           narrows the side space shrinks evenly, down to 24 pixels on a phone,
           so the record never hugs one edge. -->
      <div class="px-6 pt-10 sm:px-10 lg:px-16" data-testid="record-page-body">
      <div class="mx-auto flex w-full max-w-5xl flex-col gap-5">
        <p v-if="recordQuery.isLoading.value" class="text-sm text-muted-foreground">Loading…</p>

        <div v-else-if="isNotFound" class="flex flex-col items-start gap-3" data-testid="record-not-found">
          <p class="text-base">{{ noun }} {{ recordId }} doesn't exist or you can't see it.</p>
          <router-link :to="listPathFor(entityId)" class="text-sm underline underline-offset-4">Back to {{ entity?.label ?? 'the list' }}</router-link>
        </div>

        <div v-else-if="recordQuery.isError.value" class="flex flex-col items-start gap-3" data-testid="record-load-failed">
          <p class="text-base">Couldn't load {{ noun.toLowerCase() }} {{ recordId }}.</p>
          <button type="button" class="rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-muted" @click="recordQuery.refetch()">Retry</button>
        </div>

        <RecordBody
          v-else-if="row"
          :entity-id="entityId"
          :record-id="recordId"
          :row="row"
          :layout="layout"
          :initial-section="initialSection"
          :on-section-change="onSectionChange"
        />
      </div>
      </div>
    </div>
    <CustomizeRecordDialog
      v-if="row"
      v-model:open="customizeOpen"
      :entity-id="entityId"
      :noun="nounInSentence(noun)"
      :columns="columns"
      :layout-state="layoutState"
      :is-developer="isDeveloper"
      @closed="moreMenu?.focusTrigger()"
    />
    </div>
  </div>
</template>
